import type { Campaign } from 'c-sdk';
import type { TranslationPath } from 'c-translation';

/** What the campaign list's playout column shows. Extracted from `CampaignPlayoutsCell`. */
export type PlayoutDisplay =
    | { kind: 'percent'; value: number | string; tooltip: TranslationPath }
    | { kind: 'impactsWithoutExpected' }
    | { kind: 'none' };

/**
 * The API writes 0% when no impacts were expected, so `playout_stats` is what tells the cases
 * apart. Campaigns without it (not re-run since it was added) show `playout_percentage` as before.
 */
export const resolvePlayoutDisplay = (entity: Campaign): PlayoutDisplay => {
    const stats = entity.playout_stats;
    const overall: PlayoutDisplay = {
        kind: 'percent',
        // the cell renders nothing before a PCA has run, so this is only a type guard
        value: entity.playout_percentage ?? 0,
        tooltip: 'Modules.Main.Campaigns.List.table.playoutOverallTooltip',
    };

    if (stats == null || Number(stats.expected_impacts) > 0) return overall;

    if (Number(stats.impacts_received) > 0) return { kind: 'impactsWithoutExpected' };

    if (stats.plays_percentage != null) {
        return {
            kind: 'percent',
            value: stats.plays_percentage,
            tooltip: 'Modules.Main.Campaigns.List.table.playoutPlaysTooltip',
        };
    }

    return { kind: 'none' };
};

/** Delivered in full (100% or more) reads green, anything short of it red. */
export const playoutPercentColor = (value: number | string | null | undefined) =>
    Number(value) >= 100 ? 'success.main' : 'error.main';

/**
 * The data-status page's overall playout colour: none until the PCA has run, then as
 * `playoutPercentColor`. Extracted from `CampaignDataStatusReportingCell`.
 */
export const overallPlayoutColor = (
    entity: Pick<Campaign, 'pca_last_run_at' | 'playout_percentage'>,
) => (entity.pca_last_run_at == null ? undefined : playoutPercentColor(entity.playout_percentage));
